// src/screens/auth/SocialOnboardingScreen.js
// 74차: 신규 생성 — SNS(카카오/네이버/구글/애플) 신규가입자 전용 온보딩 화면.
// LoginScreen.js가 SNS 인증 성공 후 Firestore에 users 문서가 없으면(=신규) 여기로
// 이동시킨다. 이 시점엔 Firebase Auth엔 이미 로그인돼 있지만 Firestore 프로필은 아직
// 없는 상태 — 여기서 닉네임을 정하고 약관에 동의해야 ensureSocialUserDoc이 호출되어
// 프로필이 실제로 생성된다. 그 사이 AppStack의 "로그인되면 Main으로" 자동 이동은
// AuthContext의 authRedirectSuppressed로 막아둔 상태(LoginScreen.js가 이미 true로 세팅).
// 화면을 벗어나면(취소/뒤로가기/스와이프) 반쪽짜리 계정(Auth만 있고 프로필 없음)으로
// 남지 않도록 signOut 처리한다.
// 74차: ensureSocialUserDoc 성공 후 refreshProfile() 호출 누락 발견·수정 — 없으면
// AuthContext의 profile이 계속 null로 남아 닉네임/포인트 등이 안 채워진 채로 있었음
// 74차: 닉네임 미입력 시 "2자 이상" 경고로 막던 것 → RegisterScreen.js와 동일하게
// 선택 입력으로 바꾸고 비워두면 자동으로 랜덤 닉네임 생성
// 76차: 헤더가 아예 없던 것 → 공통 AppHeader 추가(제목만 표시). 이탈 경로는 기존 하단
// "취소" 버튼이 이미 담당하고 있어 showBack은 넣지 않음(상단·하단 이중 취소 방지).
// 77차: 닉네임 초기값을 social.displayName(SNS 실명)으로 채우던 것 → 처음부터 랜덤
// 닉네임으로 채움(실명이 그대로 닉네임으로 노출되는 것 방지, 미입력 상태로 두지 않음).
// 78차: 랜덤/보기/제출/취소 버튼을 공용 SecondaryButton/GradientButton/TextButton으로 교체

import React, { useCallback, useEffect, useState } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, ScrollView, KeyboardAvoidingView, Platform,
} from 'react-native';
import { ensureSocialUserDoc, logout } from '../../services/authService';
import { generateRandomNickname, BLOCKED_NICKNAME_WORDS } from './RegisterScreen';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton, TextButton } from '../../components/common/Buttons';
import { CheckmarkIcon } from '../../components/common/Icons';
import { COLORS } from '../../constants/theme';

const GOLD       = COLORS.gold;
const BG         = COLORS.bg;
const SURFACE    = COLORS.surface;
const BORDER     = 'rgba(212,168,67,0.2)';
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = 'rgba(255,255,255,0.35)';
const ON_GOLD    = COLORS.onGold;

const PROVIDER_LABEL = { kakao: '카카오톡', naver: '네이버', google: 'Google', apple: 'Apple' };

export default function SocialOnboardingScreen({ navigation, route }) {
  const { isLoggedIn, authRedirectSuppressed, adminRole, setAuthRedirectSuppressed, refreshProfile } = useAuth();
  const social = route?.params?.social || {};
  const [nickname, setNickname] = useState(() => generateRandomNickname());
  const [inviterUid, setInviterUid] = useState(''); // 80차 계속: 친구 초대 리워드(선택)
  const [checkedTerms,   setCheckedTerms]   = useState(false);
  const [checkedPrivacy, setCheckedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const allChecked = checkedTerms && checkedPrivacy;

  // 78차 56차: AppStack의 외부 navRef 기반 navigate/reset/goBack이 실기기에서 네이티브
  // 화면 전환을 실제로 못 걷어내는 것으로 확인돼(LoginScreen.js/RegisterScreen.js 참고),
  // 이 화면도 자기 자신의 navigation prop으로 직접 닫도록 함.
  useEffect(() => {
    if (completed && isLoggedIn && !authRedirectSuppressed) {
      navigation.goBack();
      if (adminRole === 'store' || adminRole === 'super') {
        navigation.navigate('Main');
      } else {
        // 80차 계속: 회원가입 직후 모집 게시판으로 원턴 유도 — RegisterScreen.js와 동일 패턴.
        navigation.navigate('Main', { screen: 'Home', params: { justSignedUp: true } });
      }
    }
  }, [completed, isLoggedIn, authRedirectSuppressed, adminRole]);

  // 이 화면을 완료하지 않고 벗어나면 Auth만 생성된 반쪽짜리 계정을 남기지 않도록 로그아웃
  useEffect(() => {
    const unsub = navigation.addListener('beforeRemove', () => {
      if (!completed) {
        setAuthRedirectSuppressed(false);
        logout().catch(() => {});
      }
    });
    return unsub;
  }, [navigation, completed]);

  const handleRandomNickname = useCallback(() => {
    setNickname(generateRandomNickname());
  }, []);

  const handleCancel = () => {
    navigation.goBack(); // beforeRemove가 로그아웃 처리
  };

  const handleSubmit = async () => {
    // 74차: 닉네임 미입력 시 자동 생성(RegisterScreen.js와 동일 패턴) — 원래는 빈 값이면
    // "2자 이상" 경고로 막아서, 소셜 프로필에 이름이 없는 경우(카카오 등) 사용자가 직접
    // 입력하거나 "랜덤" 버튼을 누르지 않으면 진행이 안 됐음. 이제 비워두면 그냥 자동 생성.
    const trimmed = nickname.trim();
    const finalName = trimmed || generateRandomNickname();
    if (trimmed && trimmed.length > 12) { showAlert('알림', '닉네임은 12자 이하로 입력해주세요.'); return; }
    if (BLOCKED_NICKNAME_WORDS.some(w => finalName.includes(w))) {
      showAlert('사용 불가', '"관리자" 등의 단어는 닉네임에 사용할 수 없습니다.'); return;
    }
    if (!allChecked) { showAlert('약관 동의', '이용약관 및 개인정보처리방침에 동의해주세요.'); return; }

    try {
      setLoading(true);
      await ensureSocialUserDoc({
        uid: social.uid, email: social.email, displayName: finalName,
        photoURL: social.photoURL, provider: social.provider,
        agreedAt: new Date().toISOString(),
        inviterUid: inviterUid.trim(),
      });
      await refreshProfile(); // 74차: 방금 생성한 프로필을 AuthContext에 반영 (닉네임/포인트 등이 계속 비어있던 문제)
      setCompleted(true);
      setAuthRedirectSuppressed(false); // AppStack이 Main으로 이동
    } catch (e) {
      showAlert('오류', e.message || '계정 생성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={s.container}>
      <AppHeader centerElement={<Text style={s.headerTitle}>회원가입</Text>} />
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={s.inner} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
        <Text style={s.title}>거의 다 왔어요!</Text>
        <Text style={s.desc}>
          {PROVIDER_LABEL[social.provider] || 'SNS'} 계정으로 시작하기 전{'\n'}닉네임을 정하고 약관에 동의해주세요.
        </Text>

        <Text style={s.label}>닉네임 <Text style={s.optional}>(선택)</Text></Text>
        <View style={s.nicknameRow}>
          <TextInput
            style={[s.input, s.nicknameInput]}
            value={nickname}
            onChangeText={setNickname}
            placeholder="미입력 시 랜덤 닉네임 설정"
            placeholderTextColor={TEXT_MUTED}
            autoCorrect={false}
          />
          <SecondaryButton label="🎲 랜덤" onPress={handleRandomNickname} style={s.randomBtn} />
        </View>

        {/* 80차 계속: 친구 초대 리워드 — RegisterScreen.js와 동일 패턴 */}
        <Text style={s.label}>초대코드 <Text style={s.optional}>(선택)</Text></Text>
        <TextInput
          style={s.input}
          placeholder="친구에게 받은 초대코드를 입력해주세요"
          placeholderTextColor={TEXT_MUTED}
          value={inviterUid}
          onChangeText={setInviterUid}
          autoCapitalize="none"
          autoCorrect={false}
        />

        <View style={s.divider} />

        <TouchableOpacity
          style={s.row}
          onPress={() => { setCheckedTerms(v => !v); setCheckedPrivacy(v => !v); }}
          activeOpacity={0.8}>
          <View style={[s.check, allChecked && s.checkOn]}>
            {allChecked && <CheckmarkIcon size={s.checkMark.fontSize || 12} color={s.checkMark.color} />}
          </View>
          <Text style={[s.rowText, { color: TEXT, fontWeight: '800' }]}>전체 동의</Text>
        </TouchableOpacity>

        <TouchableOpacity style={s.row} onPress={() => setCheckedTerms(v => !v)} activeOpacity={0.8}>
          <View style={[s.check, checkedTerms && s.checkOn]}>
            {checkedTerms && <CheckmarkIcon size={s.checkMark.fontSize || 12} color={s.checkMark.color} />}
          </View>
          <Text style={s.rowText}>(필수) 이용약관 동의</Text>
          <SecondaryButton label="보기" onPress={() => navigation.navigate('Terms')} style={s.viewBtn} textStyle={s.viewBtnText} />
        </TouchableOpacity>

        <TouchableOpacity style={s.row} onPress={() => setCheckedPrivacy(v => !v)} activeOpacity={0.8}>
          <View style={[s.check, checkedPrivacy && s.checkOn]}>
            {checkedPrivacy && <CheckmarkIcon size={s.checkMark.fontSize || 12} color={s.checkMark.color} />}
          </View>
          <Text style={s.rowText}>(필수) 개인정보처리방침 동의</Text>
          <SecondaryButton label="보기" onPress={() => navigation.navigate('Privacy')} style={s.viewBtn} textStyle={s.viewBtnText} />
        </TouchableOpacity>

        <Text style={s.notice}>
          만 14세 이상만 가입하실 수 있습니다.{'\n'}
          수집된 개인정보는 서비스 제공 목적으로만 사용됩니다.
        </Text>

        <GradientButton
          label={loading ? '처리 중...' : '동의하고 시작하기'}
          onPress={handleSubmit}
          disabled={!allChecked || loading}
          style={s.submitBtn}
          textStyle={s.submitBtnText}
        />

        <TextButton label="취소" onPress={handleCancel} disabled={loading} style={s.cancelBtn} textStyle={s.cancelBtnText} />
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  headerTitle: { color: TEXT, fontSize: 15, fontWeight: '800' },
  inner: { flexGrow: 1, padding: 24, paddingBottom: 48 },
  title: { fontSize: 24, fontWeight: '900', color: TEXT, marginBottom: 8, marginTop: 20 },
  desc: { fontSize: 14, color: TEXT_SEC, marginBottom: 28, lineHeight: 22 },
  label: { color: TEXT, fontSize: 13, fontWeight: '600', marginBottom: 8 },
  optional: { color: TEXT_MUTED, fontWeight: '400' },
  nicknameRow: { flexDirection: 'row', gap: 8 },
  input: { backgroundColor: '#0d0d1a', borderRadius: 10, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 14, height: 50, color: TEXT, fontSize: 14 },
  nicknameInput: { flex: 1 },
  randomBtn: { height: 50, justifyContent: 'center' },
  divider: { height: 1, backgroundColor: BORDER, marginVertical: 24 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10 },
  check: { width: 22, height: 22, borderRadius: 6, borderWidth: 1.5, borderColor: BORDER,
           justifyContent: 'center', alignItems: 'center', backgroundColor: '#0d0d1a' },
  checkOn: { backgroundColor: GOLD, borderColor: GOLD },
  checkMark: { color: ON_GOLD, fontSize: 13, fontWeight: '900' },
  rowText: { color: TEXT_SEC, fontSize: 14, flex: 1 },
  viewBtn: { paddingHorizontal: 10, paddingVertical: 4 },
  viewBtnText: { fontSize: 11 },
  notice: { color: TEXT_MUTED, fontSize: 12, lineHeight: 18, marginTop: 16, marginBottom: 28,
            backgroundColor: '#0d0d1a', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: BORDER },
  submitBtn: { marginBottom: 12 },
  submitBtnText: { fontSize: 16 },
  cancelBtn: { alignItems: 'center', paddingVertical: 12 },
  cancelBtnText: { color: TEXT_MUTED, fontSize: 14 },
});
